import { Anchor, Flex } from "@mantine/core";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  AspectRatioError,
  CheckGreenIcon,
  DPIErrorIcon,
  DPILowIcon,
  InfoIconBlue,
  UpRightSquare,
} from "../../assets/svgs";
import { AspectRatio } from "../../interfaces";
import { round } from "../../utils";

type Props = {
  dpi?: number;
  ratio?: {
    x: number;
    y: number;
    fit: boolean;
  };
  productRatio: {
    x: number;
    y: number;
  };
  aspectRatio?: AspectRatio;
  numPages?: number;
};

const ImageDetail: React.FC<Props> = ({
  dpi,
  ratio,
  aspectRatio,
  numPages,
  productRatio: { x, y },
}) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="image-detail mt-10">
        <span>
          <strong>DPI</strong>
          <span className="ml-5">{t("minRecommendedDpi")}</span>
        </span>
        <span className="image-detail__icon">
          {!!dpi && (
            <>
              {dpi <= 30 ? (
                <DPIErrorIcon />
              ) : dpi <= 50 ? (
                <DPILowIcon />
              ) : (
                <CheckGreenIcon />
              )}{" "}
              {dpi}
            </>
          )}
        </span>
      </div>
      <div className="image-detail">
        <span>
          <strong>{t("aspectRatio")}</strong>
          <span className="ml-5">
            {round(x, 2)}:{round(y, 2)}
          </span>
        </span>
        <span className="image-detail__icon">
          {!!aspectRatio ? (
            <>
              {!!aspectRatio?.x && (
                <>
                  {aspectRatio.fit ? <CheckGreenIcon /> : <AspectRatioError />}{" "}
                  {round(aspectRatio.x, 2)}:{round(aspectRatio.y, 2)}
                </>
              )}
            </>
          ) : (
            <>
              {!!ratio?.x && (
                <>
                  {ratio.fit ? <CheckGreenIcon /> : <AspectRatioError />}{" "}
                  {ratio.fit ? (
                    <>
                      {ratio.x}:{ratio.y}
                    </>
                  ) : (
                    <>
                      {ratio.x}:{ratio.y}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </span>
      </div>
      <div className="image-error">
        {!!dpi && (
          <div className="row row-v-center gap-4">
            {dpi <= 30 ? (
              <span className="text-error">{t("dpiTooSmall")}</span>
            ) : dpi <= 50 ? (
              <span className="text-warning">{t("dpiSmall")}</span>
            ) : (
              <span className="text-success">{t("dpiGood")}</span>
            )}
            <div className="popover">
              <div className="popover__target">
                <InfoIconBlue />
              </div>
              <div className="popover__content">
                {t("dpiInfo")}
                <Anchor href={t("learnMoreUrl")} target="_blank">
                  <Flex
                    mt={4}
                    gap={4}
                    justify="left"
                    w="fit-content"
                    align="center"
                    className="cursor-pointer"
                  >
                    <span className="f-w-700 color-primary">
                      {t("learnMore")}
                    </span>
                    <UpRightSquare id="dpi" />
                  </Flex>
                </Anchor>
              </div>
            </div>
          </div>
        )}
        <div className="row row-v-center gap-4">
          {!!aspectRatio ? (
            <>
              {!!aspectRatio && (
                <>
                  {aspectRatio.fit ? (
                    <span className="text-success">{t("aspectRatioFits")}</span>
                  ) : (
                    <span className="text-error">
                      {t("aspectRatioNotFits")}
                    </span>
                  )}
                  <div className="popover">
                    <div className="popover__target">
                      <InfoIconBlue />
                    </div>
                    <div className="popover__content">
                      {t("ratioInfo")}
                      <Anchor href={t("learnMoreUrl")} target="_blank">
                        <Flex
                          mt={4}
                          gap={4}
                          justify="left"
                          w="fit-content"
                          align="center"
                          className="cursor-pointer"
                        >
                          <span className="f-w-700 color-primary">
                            {t("learnMore")}
                          </span>
                          <UpRightSquare id="ratio" />
                        </Flex>
                      </Anchor>
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              {!!ratio?.x && (
                <>
                  {ratio.fit ? (
                    <span className="text-success">{t("aspectRatioFits")}</span>
                  ) : (
                    <span className="text-error">
                      {t("aspectRatioNotFits")}
                    </span>
                  )}
                  <div className="popover">
                    <div className="popover__target">
                      <InfoIconBlue />
                    </div>
                    <div className="popover__content">
                      {t("ratioInfo")}
                      <Anchor href={t("learnMoreUrl")} target="_blank">
                        <Flex
                          mt={4}
                          gap={4}
                          justify="left"
                          w="fit-content"
                          align="center"
                          className="cursor-pointer"
                        >
                          <span className="f-w-700 color-primary">
                            {t("learnMore")}
                          </span>
                          <UpRightSquare id="ratio" />
                        </Flex>
                      </Anchor>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        {!!numPages && (
          <>
            {numPages > 1 && (
              <span className="text-error">{t("moreThan1Page")}</span>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default ImageDetail;
